import { Button as ButtonPrimitive } from '@base-ui/react/button';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from 'utils/utils';
import Loader from 'components/Loader';

const buttonVariants = cva(
  "font-[MuseoSansCyrl700] hover:bg-brand-yellow/80 disabled:cursor-not-allowed group/button inline-flex shrink-0 items-center justify-center rounded-lg border border-transparent bg-clip-padding text-base whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: 'bg-brand-yellow text-black [a]:hover:bg-primary/80 relative ',
        outline:
          'border-brand-brown bg-transparent text-brand-brown hover:bg-brand-brown hover:text-white aria-expanded:bg-brand-brown aria-expanded:text-white',
        secondary:
          'bg-brand-brown text-white hover:bg-secondary-hover aria-expanded:bg-brand-brown in-[a:hover]:bg-secondary-hover',
        ghost:
          'hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50',
        destructive: 'hover:bg-error200/40 bg-error100 text-red',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'gap-1.5 px-4 py-3 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2',
        xs: "h-6 gap-1 rounded-[min(var(--radius-md),10px)] px-2 text-xs in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-7 gap-1 rounded-[min(var(--radius-md),12px)] px-2.5 text-[0.8rem] in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        lg: 'h-9 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3',
        icon: 'size-8',
        'icon-xs':
          "size-6 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3",
        'icon-sm': 'size-7 rounded-[min(var(--radius-md),12px)] in-data-[slot=button-group]:rounded-lg',
        'icon-lg': 'size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

interface Props extends ButtonPrimitive.Props, VariantProps<typeof buttonVariants> {
  loading?: boolean;
}

function Button({ className, variant = 'default', size = 'default', children, loading, ...props }: Props) {
  return (
    <ButtonPrimitive
      data-slot='button'
      className={cn('relative', buttonVariants({ variant, size }), className)}
      {...props}
    >
      <span className={cn('inline-flex items-center justify-center gap-2', loading && 'invisible')}>{children}</span>

      {loading && (
        <span className='absolute inset-0 flex items-center justify-center'>
          <Loader />
        </span>
      )}
    </ButtonPrimitive>
  );
}

export { Button, buttonVariants };
